import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { X, Plus, Trash2, Bell, BellOff } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Switch } from './ui/switch';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AlertsPanel = ({ onClose, cryptoList }) => {
  const [alerts, setAlerts] = useState([]);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [selectedCrypto, setSelectedCrypto] = useState('');
  const [targetPrice, setTargetPrice] = useState('');
  const [condition, setCondition] = useState('above');
  const [loading, setLoading] = useState(false);
  const [permissionGranted, setPermissionGranted] = useState(false);

  useEffect(() => {
    fetchAlerts();
    checkNotificationPermission();
    
    // Check alerts every 30 seconds
    const interval = setInterval(() => {
      checkAlerts();
    }, 30000);
    
    return () => clearInterval(interval);
  }, []);

  const checkNotificationPermission = async () => {
    if ('Notification' in window) {
      if (Notification.permission === 'granted') {
        setPermissionGranted(true);
      } else if (Notification.permission !== 'denied') {
        const permission = await Notification.requestPermission();
        setPermissionGranted(permission === 'granted');
      }
    }
  };

  const fetchAlerts = async () => {
    try {
      const response = await axios.get(`${API}/alerts`);
      if (response.data) {
        setAlerts(response.data);
      }
    } catch (error) {
      console.error('Error fetching alerts:', error);
    }
  };

  const checkAlerts = async () => {
    try {
      const response = await axios.post(`${API}/alerts/check`);
      if (response.data.success && response.data.triggered.length > 0) {
        response.data.triggered.forEach(alert => {
          showNotification(alert);
        });
        fetchAlerts();
      }
    } catch (error) {
      console.error('Error checking alerts:', error);
    }
  };

  const showNotification = (alert) => {
    const message = `${alert.name} is now ${alert.condition} $${alert.target_price.toFixed(2)}. Current price: $${alert.current_price.toFixed(2)}`;
    
    toast.success('Price Alert Triggered!', {
      description: message,
      duration: 10000
    });

    if (permissionGranted && 'Notification' in window) {
      new Notification('Crypto Price Alert', {
        body: message,
        icon: '/favicon.ico',
        badge: '/favicon.ico'
      });
    }
  };

  const createAlert = async () => {
    if (!selectedCrypto || !targetPrice) {
      toast.error('Please fill in all fields');
      return;
    }

    const crypto = cryptoList.find(c => c.symbol === selectedCrypto);
    if (!crypto) return;

    try {
      setLoading(true);
      const response = await axios.post(`${API}/alerts`, {
        symbol: crypto.symbol,
        name: crypto.name,
        target_price: parseFloat(targetPrice),
        condition: condition
      });

      if (response.data) {
        toast.success('Alert created successfully!');
        fetchAlerts();
        setShowCreateForm(false);
        setSelectedCrypto('');
        setTargetPrice('');
        setCondition('above');
      }
    } catch (error) {
      console.error('Error creating alert:', error);
      toast.error('Failed to create alert');
    } finally {
      setLoading(false);
    }
  };

  const deleteAlert = async (alertId) => {
    try {
      await axios.delete(`${API}/alerts/${alertId}`);
      toast.success('Alert deleted');
      fetchAlerts();
    } catch (error) {
      console.error('Error deleting alert:', error);
      toast.error('Failed to delete alert');
    }
  };

  const toggleAlert = async (alertId, isActive) => {
    try {
      await axios.patch(`${API}/alerts/${alertId}`, {
        is_active: !isActive
      });
      toast.success(isActive ? 'Alert disabled' : 'Alert enabled');
      fetchAlerts();
    } catch (error) {
      console.error('Error toggling alert:', error);
      toast.error('Failed to update alert');
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="alerts-panel-overlay"
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-2xl max-h-[90vh] overflow-y-auto"
        data-testid="alerts-panel"
      >
        <Card className="border-slate-200 dark:border-slate-800">
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle className="text-2xl">Price Alerts</CardTitle>
              <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
                {permissionGranted ? '🔔 Notifications enabled' : '🔕 Enable browser notifications for alerts'}
              </p>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={onClose}
              data-testid="alerts-panel-close"
            >
              <X className="w-5 h-5" />
            </Button>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Create Alert Button */}
            {!showCreateForm && (
              <Button
                onClick={() => setShowCreateForm(true)}
                className="w-full bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700"
                data-testid="create-alert-button"
              >
                <Plus className="w-4 h-4 mr-2" />
                Create New Alert
              </Button>
            )}

            {/* Create Alert Form */}
            {showCreateForm && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="p-4 border border-slate-200 dark:border-slate-800 rounded-lg space-y-4"
                data-testid="create-alert-form"
              >
                <div className="space-y-2">
                  <Label>Cryptocurrency</Label>
                  <Select value={selectedCrypto} onValueChange={setSelectedCrypto}>
                    <SelectTrigger data-testid="alert-crypto-select">
                      <SelectValue placeholder="Select a cryptocurrency" />
                    </SelectTrigger>
                    <SelectContent>
                      {cryptoList.slice(0, 20).map((crypto) => (
                        <SelectItem key={crypto.id} value={crypto.symbol}>
                          {crypto.name} ({crypto.symbol})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Target Price</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={targetPrice}
                      onChange={(e) => setTargetPrice(e.target.value)}
                      placeholder="0.00"
                      data-testid="alert-target-price"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Condition</Label>
                    <Select value={condition} onValueChange={setCondition}>
                      <SelectTrigger data-testid="alert-condition-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="above">Above</SelectItem>
                        <SelectItem value="below">Below</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="flex gap-2">
                  <Button
                    onClick={createAlert}
                    disabled={loading}
                    className="flex-1 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700"
                    data-testid="alert-submit"
                  >
                    {loading ? 'Creating...' : 'Create Alert'}
                  </Button>
                  <Button
                    onClick={() => {
                      setShowCreateForm(false);
                      setSelectedCrypto('');
                      setTargetPrice('');
                      setCondition('above');
                    }}
                    variant="outline"
                    data-testid="alert-cancel"
                  >
                    Cancel
                  </Button>
                </div>
              </motion.div>
            )}

            {/* Alerts List */}
            <div className="space-y-2" data-testid="alerts-list">
              {alerts.length === 0 ? (
                <div className="text-center py-8 text-slate-500 dark:text-slate-400">
                  <Bell className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>No alerts created yet</p>
                </div>
              ) : (
                alerts.map((alert, index) => (
                  <motion.div
                    key={alert.id}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.05 }}
                    className="p-4 border border-slate-200 dark:border-slate-800 rounded-lg flex items-center justify-between"
                    data-testid={`alert-item-${alert.symbol}`}
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-slate-900 dark:text-slate-100">{alert.name}</span>
                        <span className="text-sm text-slate-500 dark:text-slate-400">({alert.symbol})</span>
                      </div>
                      <div className="text-sm text-slate-600 dark:text-slate-400 mt-1">
                        Alert when price goes {alert.condition} ${alert.target_price.toFixed(2)}
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <Switch
                        checked={alert.is_active}
                        onCheckedChange={() => toggleAlert(alert.id, alert.is_active)}
                        data-testid={`alert-toggle-${alert.symbol}`}
                      />
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => deleteAlert(alert.id)}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-950"
                        data-testid={`alert-delete-${alert.symbol}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </motion.div>
                ))
              )}
            </div>
          </CardContent>
        </Card>
      </motion.div>
    </motion.div>
  );
};

export default AlertsPanel;
